import React, { useRef, useEffect, useState, forwardRef } from "react";
import { Card, Text, Pill } from "@mantine/core";
import { gsap } from "gsap";
import { IconArrowUpRight } from "@tabler/icons-react";
import "./Project.css";

const ProjectContent = ({ project }) => (
  <div className="project-item-content">
    <img
      className="project-item-image"
      src={project.imageLink || `/images/${project.id}.png`}
      alt={project.title}
    />
    <div className="project-item-details">
      <div className="project-item-title-container">
        <div className="project-item-title">{project.title}</div>
        <IconArrowUpRight className="arrow-up-icon" />
      </div>
      <Text className="project-item-description">{project.description}</Text>
      <Pill.Group className="project-pills">
        {project.techStack?.map((tech) => (
          <Pill key={tech} size="sm">
            {tech}
          </Pill>
        ))}
      </Pill.Group>
    </div>
  </div>
);

const ProjectContentRelative = forwardRef(
  ({ project, spotlightCoordinates }, ref) => (
    <div
      ref={ref}
      className="project-item-content-relative"
      style={{
        clipPath: `circle(50px at ${spotlightCoordinates.x}% ${spotlightCoordinates.y}%)`,
      }}
    >
      <img
        className="project-item-image"
        src={project.imageLink || `/images/${project.id}.png`}
        alt={project.title}
      />
      <div className="project-item-details">
        <div className="project-item-title-container">
          <div className="project-item-title">{project.title}</div>
        </div>
        <Text className="project-item-description-relative">{project.description}</Text>
        <Pill.Group className="project-pills">
          {project.techStack?.map((tech) => (
            <Pill key={tech} size="sm">
              {tech}
            </Pill>
          ))}
        </Pill.Group>
      </div>
    </div>
  )
);

const useSpotlight = (containerRef, spotlightRef) => {
  const [spotlightCoordinates, setSpotlightCoordinates] = useState({ x: 50, y: 0 });

  const handleMouseMove = (e) => {
    if (!spotlightRef.current || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const xPercent = (x / rect.width) * 100;
    const yPercent = (y / rect.height) * 100;

    setSpotlightCoordinates({ x: xPercent, y: yPercent });

    gsap.to(spotlightRef.current, {
      clipPath: `circle(50px at ${xPercent}% ${yPercent}%)`,
      opacity: 1,
      duration: 0,
      ease: "sine.out",
      transform: "none",
    });
  };

  return { spotlightCoordinates, handleMouseMove };
};

const ProjectItem = ({ project, setHoveredProject, onProjectSelect }) => {
  const [isHovered, setIsHovered] = useState(false);
  const containerRef = useRef(null);
  const spotlightRef = useRef(null);

  const handleProjectItemClick = () => {
    if (onProjectSelect) {
      onProjectSelect(project);
    }
    console.log(project)
  };

  const { spotlightCoordinates, handleMouseMove } = useSpotlight(containerRef, spotlightRef);

  useEffect(() => {
    setHoveredProject(isHovered ? project.id : null);
  }, [isHovered, setHoveredProject, project]);

  return (
    <Card className="project-item-card" padding="lg" radius="md" withBorder>
      <div
        onClick={handleProjectItemClick}
        ref={containerRef}
        className="project-item"
        onMouseMove={handleMouseMove}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <ProjectContent project={project} />
        {isHovered && (
          <ProjectContentRelative
            ref={spotlightRef}
            project={project}
            spotlightCoordinates={spotlightCoordinates}
          />
        )}
      </div>
    </Card>
  );
};

export default ProjectItem;
